import React from 'react';
import { ArrowLeft, Shield, Mail, Phone, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "April 23, 2026";
  const companyName = "CREATRVERSE";
  const companyEmail = "darren@creatrverse.com";
  const websiteUrl = "https://operatr.me";

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <Shield className="text-purple-500" size={24} />
              <h1 className="text-xl font-bold">Privacy Policy</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-invert max-w-none">
          <p className="text-gray-400 mb-8">Last Updated: {lastUpdated}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              Welcome to {companyName} ("we," "our," or "us"). We are committed to protecting your personal information 
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our website, mobile application, and services (collectively, the "Services").
            </p>
            <p className="text-gray-300 leading-relaxed mt-4">
              Please read this Privacy Policy carefully. By accessing or using our Services, you acknowledge that you 
              have read, understood, and agree to be bound by this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-white">2.1 Personal Information You Provide</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              We collect personal information that you voluntarily provide when you:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Register for an account (name, email address, password)</li>
              <li>Complete your profile (phone number, business information, profile photo)</li>
              <li>Make purchases (payment information, billing address)</li>
              <li>Contact us for support (communication history)</li>
              <li>Subscribe to our newsletter or marketing communications</li>
              <li>Connect third-party accounts (Google, email providers)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6 text-white">2.2 Information Automatically Collected</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              When you access our Services, we automatically collect certain information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Device information (device type, operating system, unique device identifiers)</li>
              <li>Log data (IP address, browser type, pages visited, time and date of visits)</li>
              <li>Location data (general geographic location based on IP address)</li>
              <li>Usage data (features used, actions taken, time spent on pages)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6 text-white">2.3 Information from Third Parties</h3>
            <p className="text-gray-300 leading-relaxed">
              We may receive information about you from third-party services you connect to our platform, such as:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-4">
              <li>Google (when you sign in with Google or connect Gmail)</li>
              <li>Email service providers (Nylas, Gmail API)</li>
              <li>Payment processors (Stripe)</li>
              <li>Calendar services (Google Calendar)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">3. How We Use Your Information</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Provide Services:</strong> To create and manage your account, process transactions, and deliver our services</li>
              <li><strong>Communications:</strong> To send you service-related notifications, updates, and marketing communications (with your consent)</li>
              <li><strong>Personalization:</strong> To customize your experience and provide content relevant to your interests</li>
              <li><strong>Analytics:</strong> To understand how users interact with our Services and improve functionality</li>
              <li><strong>Security:</strong> To detect, prevent, and address fraud, abuse, and security issues</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Customer Support:</strong> To respond to your inquiries and resolve issues</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">4. How We Share Your Information</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (hosting, payment processing, analytics, customer support)</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Legal Requirements:</strong> When required by law or to respond to legal processes</li>
              <li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property</li>
              <li><strong>With Your Consent:</strong> When you have given us explicit permission to share your information</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              We do not sell your personal information to third parties for their marketing purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">5. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Our Services integrate with the following third-party services:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Google Services:</strong> For authentication, email (Gmail API), and calendar integration</li>
              <li><strong>Stripe:</strong> For payment processing</li>
              <li><strong>Twilio:</strong> For SMS and communication services</li>
              <li><strong>Nylas:</strong> For email synchronization</li>
              <li><strong>OpenAI:</strong> For AI-powered features</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              These third-party services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">6. Data Retention</h2>
            <p className="text-gray-300 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
              Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer 
              need your information, we will securely delete or anonymize it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">7. Data Security</h2>
            <p className="text-gray-300 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-4">
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure password hashing</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Monitoring for suspicious activities</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">8. Your Rights and Choices</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-out:</strong> Opt out of marketing communications</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              To exercise these rights, please contact us at <a href={`mailto:${companyEmail}`} className="text-purple-400 hover:text-purple-300">{companyEmail}</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">9. Cookies and Tracking Technologies</h2>
            <p className="text-gray-300 leading-relaxed">
              We use cookies and similar tracking technologies to collect information about your browsing activities. 
              You can control cookies through your browser settings. However, disabling cookies may limit your ability 
              to use certain features of our Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">10. Children's Privacy</h2>
            <p className="text-gray-300 leading-relaxed">
              Our Services are not directed to children under the age of 13 (or 16 in the European Economic Area). 
              We do not knowingly collect personal information from children. If we become aware that we have collected 
              personal information from a child, we will take steps to delete such information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">11. International Data Transfers</h2>
            <p className="text-gray-300 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws. We take appropriate safeguards to ensure your 
              information remains protected in accordance with this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">12. Changes to This Privacy Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you 
              to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">13. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-white/5 border border-white/20 rounded-xl p-6 space-y-3">
              <div className="flex items-center gap-3">
                <Globe size={18} className="text-purple-400" />
                <span className="text-gray-300">{companyName}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-purple-400" />
                <a href={`mailto:${companyEmail}`} className="text-purple-400 hover:text-purple-300">{companyEmail}</a>
              </div>
              <div className="flex items-center gap-3">
                <Globe size={18} className="text-purple-400" />
                <a href={websiteUrl} className="text-purple-400 hover:text-purple-300">{websiteUrl}</a>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/20 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} {companyName}. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
